import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Search, Instagram } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SearchOverlay from "@/components/SearchOverlay";
import NewsletterInline from "@/components/NewsletterInline";
import NewsletterModal from "@/components/NewsletterModal";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/letter", label: "A Letter" },
  { to: "/treatments", label: "Treatments" },
  { to: "/sanctuary", label: "Women's Sanctuary" },
  { to: "/art", label: "Art by Anissa" },
  { to: "/journal", label: "Journal" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const isHome = location.pathname === "/";
  // Home hero is now LIGHT fabric — dark text reads best. So we keep the standard dark header.
  const isHomeHero = false;
  const headerInk = isHomeHero ? "text-[#FAF8F2]" : "text-[#2E2B27]";
  const headerInkSoft = isHomeHero ? "text-[#FAF8F2]/80 hover:text-[#FAF8F2]" : "text-[#55524C] hover:text-[#2E2B27]";

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
          scrolled ? "bg-[#F7F6F2]/90 backdrop-blur-md border-b border-[#D5CDBF]" : "bg-transparent"
        }`}
      >
        <div
          className="mx-auto flex items-center justify-between h-[72px] lg:h-[88px]"
          style={{
            width: "min(100% - 40px, 1440px)",
            gap: "clamp(20px, 3vw, 56px)",
          }}
        >
          <div className="flex items-center min-w-0" style={{ gap: "clamp(16px, 2vw, 32px)" }}>
            <Link to="/" data-testid="logo-link" className="flex flex-col items-start leading-none shrink-0">
              <span className="serif text-[24px] sm:text-[26px] min-[1440px]:text-[28px] tracking-[0.02em] text-[#3A342E]" style={{ fontWeight: 400 }}>
                Studio A
              </span>
              <span className="lg:hidden text-[9px] uppercase tracking-[0.28em] mt-1.5 text-[#55524C] whitespace-nowrap">
                Bondi Beach, Sydney
              </span>
            </Link>
            <span aria-hidden="true" className="hidden lg:block w-px h-9 bg-[#D5CDBF] shrink-0" />
            <div data-testid="brand-context" className="hidden lg:flex flex-col gap-[5px] shrink-0" style={{ lineHeight: 1.35 }}>
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#2F2C28] font-medium whitespace-nowrap">
                Bondi Beach
              </span>
              <span className="serif italic text-[13px] text-[#2F2C28] whitespace-nowrap block min-[1400px]:hidden min-[1600px]:block">
                A private space where skin meets stillness
              </span>
            </div>
          </div>

          <nav
            className="hidden min-[1400px]:flex items-center whitespace-nowrap shrink-0"
            style={{ gap: "clamp(18px, 1.7vw, 30px)" }}
          >
            {NAV.slice(1, 8).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `whitespace-nowrap text-[11px] uppercase tracking-[0.18em] 2xl:tracking-[0.2em] font-medium link-underline ${
                    isActive ? headerInk : headerInkSoft
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center justify-end gap-2 min-[1440px]:gap-3 shrink-0">
            <button
              aria-label="Search"
              data-testid="search-trigger"
              onClick={() => setSearchOpen(true)}
              className="h-10 w-10 grid place-items-center transition-colors text-[#2E2B27] hover:text-[#A96A53]"
            >
              <Search size={18} strokeWidth={1.4} />
            </button>
            <div className="hidden md:block shrink-0">
              <Link
                to="/book"
                data-testid="header-book-btn"
                className="btn-primary header-cta whitespace-nowrap"
              >
                Begin Your Ritual
              </Link>
            </div>
            <button
              aria-label="Menu"
              data-testid="menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className={`min-[1400px]:hidden h-10 w-10 grid place-items-center ${headerInk}`}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="mobile-menu"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="min-[1400px]:hidden bg-[#F7F6F2] border-t border-[#D5CDBF]"
            >
              <div className="px-6 py-8 flex flex-col gap-5">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                    className="serif text-3xl text-[#2E2B27]"
                  >
                    {n.label}
                  </NavLink>
                ))}
                <Link
                  to="/book"
                  data-testid="mobile-book-btn"
                  className="btn-primary mt-4 self-start"
                >
                  Begin Your Ritual
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main data-testid="main-content" className={isHome ? "" : "pt-[72px] lg:pt-[88px]"}>
        {children}
      </main>

      <Footer />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <NewsletterModal />
    </>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="mt-32 border-t border-[#D5CDBF] bg-[#F0EDE6]">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex flex-col items-start leading-none">
            <span className="serif text-[36px] text-[#3A342E]" style={{ fontWeight: 400 }}>Studio A</span>
            <span className="text-[10px] uppercase tracking-[0.32em] text-[#6E6A63] mt-2">Bondi Beach, Sydney</span>
          </div>
          <p className="serif italic text-xl mt-6 text-[#55524C]">Where Skin Meets Stillness</p>
          <p className="mt-8 max-w-md text-[15px] leading-relaxed text-[#55524C]">
            A private women's facial studio in Bondi Beach. One woman at a time. By appointment only — come exactly as you are.
          </p>
          <div className="mt-10">
            <NewsletterInline />
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="label-eyebrow">Visit</div>
          <p className="mt-4 text-[15px] leading-relaxed text-[#2E2B27]">
            Bondi Beach<br />
            Sydney, Australia<br />
            By appointment only
          </p>
          <p className="mt-3 text-[12px] text-[#55524C] italic">
            The exact address is shared on confirmation.
          </p>
        </div>

        <div className="md:col-span-2">
          <div className="label-eyebrow">Studio</div>
          <ul className="mt-4 space-y-2 text-[14px] text-[#2E2B27]">
            <li><Link to="/treatments" className="link-underline">Treatments</Link></li>
            <li><Link to="/sanctuary" className="link-underline">Women's Sanctuary</Link></li>
            <li><Link to="/art" className="link-underline">Art by Anissa</Link></li>
            <li><Link to="/journal" className="link-underline">Journal</Link></li>
            <li><Link to="/faq" className="link-underline">FAQ</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="label-eyebrow">Elsewhere</div>
          <ul className="mt-4 space-y-2 text-[14px] text-[#2E2B27]">
            <li>
              <a
                href="https://www.instagram.com/studio_anissa/"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                className="link-underline inline-flex items-center gap-2"
              >
                <Instagram size={14} strokeWidth={1.4} /> Instagram
              </a>
            </li>
            <li><Link to="/contact" className="link-underline">Contact</Link></li>
            <li>
              <Link
                to="/book"
                data-testid="footer-book"
                className="link-underline"
              >
                Book
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#D5CDBF]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12px] text-[#55524C]">
          <p>© {new Date().getFullYear()} Studio A. All rights reserved.</p>
          <p className="serif italic">Anissa · Art · Awareness · Alignment</p>
        </div>
      </div>
    </footer>
  );
}
